import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { IconAnimation, Introduction, List, spacing, styled } from '@lessonup/ui-components';
import React, { PropsWithChildren } from 'react';
import { Trans } from 'react-i18next';
import { i18nextNamespace } from '../../../../Editor.utils';
import { useEditorTranslation } from '../../../../hooks/useEditorTranslation';

export interface AnimationListProps {
  sortableIds: string[];
  onReorder?(activeId: string, overId: string): void;
  canAddAnimation: boolean;
}

/**
 * Shows the list of animations for a pin. The list shows the order of the animations is in.
 * This list can contain animations from different pin components in the pin.
 */
export function AnimationList({
  sortableIds,
  onReorder,
  children,
  canAddAnimation,
}: PropsWithChildren<AnimationListProps>) {
  const { t } = useEditorTranslation();
  const dropItem = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || over.id === active.id) return;

    onReorder?.(active.id, over.id);
  };

  if (React.Children.count(children) === 0) {
    return (
      <StyledMessage
        alignment="center"
        size="S"
        icon={<IconAnimation style={{ width: '1rem', height: '1rem' }} />}
        headline={t('contextSidebar.panes.animations.animateComponent.title')}
        bodyText={
          <Trans
            i18nKey={
              canAddAnimation
                ? 'contextSidebar.panes.animations.animateComponent.bodyText'
                : 'contextSidebar.panes.animations.animateComponentInvalidSelection.bodyText'
            }
            ns={i18nextNamespace}
            components={[<strong key="0">{'content'}</strong>]}
          />
        }
      />
    );
  }

  return (
    <DndContext onDragEnd={dropItem} modifiers={[restrictToVerticalAxis, restrictToParentElement]}>
      <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
        <List>{children}</List>
      </SortableContext>
    </DndContext>
  );
}

const StyledMessage = styled(Introduction)`
  padding: ${spacing.size12};
`;
